import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  lessonAPI,
  assetAPI,
  unitGuideAPI,
  formulaAPI,
  overlayAPI,
  teacherAPI,
} from "../services/api";
import ResourceSnapper from "./ResourceSnapper";
import { getGuideByCourseAndUnit } from "../data/unitGuides";
import { BlockMath } from "react-katex";
import "./DeepDiveWorkspace.css";

const COURSE_FORMULAS = {
  economics: [
    { label: "Inflation Rate", latex: "\\text{Inflation} = \\frac{CPI_t - CPI_{t-1}}{CPI_{t-1}} \\times 100" },
    { label: "Unemployment Rate", latex: "\\text{Unemployment} = \\frac{\\text{Unemployed}}{\\text{Labor Force}} \\times 100" },
    {
      label: "Labor Force Participation",
      latex: "\\text{LFPR} = \\frac{\\text{Labor Force}}{\\text{Working-Age Population}} \\times 100",
    },
    {
      label: "Real Interest Rate",
      latex: "r_{real} \\approx r_{nominal} - \\pi",
    },
  ],
  math: [
    { label: "Slope", latex: "m = \\frac{y_2-y_1}{x_2-x_1}" },
    { label: "Distance Formula", latex: "d = \\sqrt{(x_2-x_1)^2 + (y_2-y_1)^2}" },
    { label: "Quadratic Formula", latex: "x = \\frac{-b \\pm \\sqrt{b^2-4ac}}{2a}" },
    { label: "Pythagorean Theorem", latex: "a^2 + b^2 = c^2" },
  ],
  physics: [
    { label: "Wave Speed", latex: "v = f \\times \\lambda" },
    { label: "Wave Period", latex: "T = \\frac{1}{f}" },
    { label: "Frequency", latex: "f = \\frac{1}{T}" },
    { label: "Wavelength", latex: "\\lambda = \\frac{v}{f}" },
  ],
};

const resolveFormulaDomain = (courseName = "") => {
  const normalized = String(courseName).toLowerCase();
  if (!normalized) return null;

  if (normalized.includes("economics")) return "economics";

  if (
    normalized.includes("math")
    || normalized.includes("mathematics")
    || normalized.includes("algebra")
    || normalized.includes("geometry")
    || normalized.includes("calculus")
    || normalized.includes("statistics")
    || normalized.includes("trigonometry")
    || normalized.includes("precalculus")
  ) {
    return "math";
  }

  if (normalized.includes("physics")) return "physics";

  return null;
};

const toAssetUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = process.env.PUBLIC_URL || "";
  return `${base}${url}`;
};

const normalizeText = (value) => String(value || "").toLowerCase();
const normalizeFormulaLatex = (latex) => String(latex || "").replace(/\\\\(?=[A-Za-z])/g, "\\");
const toTextArray = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }
  if (!value) return [];
  return String(value)
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const buildStrategyResourceNote = (strategyRow, strategy, learnMore) => {
  const title = String(strategy?.title || strategyRow?.title || "Untitled strategy").trim();
  const summary = String(learnMore?.summary || strategyRow?.description || "").trim();
  const sampleUsage = String(learnMore?.sample_usage || "").trim();
  const steps = toTextArray(learnMore?.how_to_use_steps);
  const lookFors = toTextArray(learnMore?.look_fors);

  const lines = [`Strategy: ${title}`];
  if (summary) lines.push(summary);
  if (sampleUsage) lines.push(`Sample usage: ${sampleUsage}`);
  if (steps.length) {
    lines.push("How to use:");
    steps.forEach((step, index) => lines.push(`${index + 1}. ${step}`));
  }
  if (lookFors.length) {
    lines.push("Look-fors:");
    lookFors.forEach((item) => lines.push(`- ${item}`));
  }
  return lines.join("\n");
};

const formatDateTime = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const lessonSortTuple = (chapterRef = "") => {
  const numericParts = String(chapterRef)
    .match(/\d+/g)
    ?.map((value) => Number(value))
    .filter((value) => Number.isFinite(value));

  if (!numericParts || numericParts.length === 0) {
    return [Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER];
  }

  return [
    numericParts[0] ?? Number.MAX_SAFE_INTEGER,
    numericParts[1] ?? Number.MAX_SAFE_INTEGER,
  ];
};

const compareLessonsForNavigation = (a, b) => {
  const [aChapter, aSubchapter] = lessonSortTuple(a?.chapter_ref);
  const [bChapter, bSubchapter] = lessonSortTuple(b?.chapter_ref);

  if (aChapter !== bChapter) return aChapter - bChapter;
  if (aSubchapter !== bSubchapter) return aSubchapter - bSubchapter;

  const titleCmp = String(a?.lesson_title || "").localeCompare(
    String(b?.lesson_title || ""),
  );
  if (titleCmp !== 0) return titleCmp;

  return String(a?.id || "").localeCompare(String(b?.id || ""));
};

const getRelevantGuideSections = (guide, lesson) => {
  if (!guide || !Array.isArray(guide.sections) || !lesson) return [];

  const chapterRef = normalizeText(lesson.chapter_ref);
  const lessonTitle = normalizeText(lesson.lesson_title);
  const chapterNumbers = new Set(
    [...chapterRef.matchAll(/chapter\s*(\d+)/gi)].map((m) => m[1]),
  );

  const matched = guide.sections.filter((section) => {
    const sectionId = normalizeText(section.id);
    const heading = normalizeText(section.heading);
    const haystack = `${sectionId} ${heading} ${normalizeText((section.bullets || []).join(" "))}`;

    if (chapterNumbers.size > 0) {
      for (const chapterNumber of chapterNumbers) {
        if (
          haystack.includes(`chapter-${chapterNumber}`)
          || haystack.includes(`chapter ${chapterNumber}`)
        ) {
          return true;
        }
      }
    }

    if (!lessonTitle) return false;
    const significantWords = lessonTitle
      .split(/\s+/)
      .map((word) => word.replace(/[^a-z0-9]/g, ""))
      .filter((word) => word.length >= 5);
    const overlapCount = significantWords.reduce(
      (count, word) => (haystack.includes(word) ? count + 1 : count),
      0,
    );
    return overlapCount >= 2;
  });

  if (matched.length > 0) return matched;

  return guide.sections.filter(
    (section) => !normalizeText(section.id).includes("full-transcript"),
  );
};

const DeepDiveWorkspace = ({ lessonId, onClose, onNavigateLesson }) => {
  const [lesson, setLesson] = useState(null);
  const [assets, setAssets] = useState([]);
  const [guide, setGuide] = useState(null);
  const [guideTranscript, setGuideTranscript] = useState("");
  const [scopedFormulas, setScopedFormulas] = useState([]);
  const [personalResources, setPersonalResources] = useState([]);
  const [loadingPersonalResources, setLoadingPersonalResources] = useState(false);
  const [personalResourcesError, setPersonalResourcesError] = useState("");
  const [deletingResourceId, setDeletingResourceId] = useState(null);
  const [unitLessonSequence, setUnitLessonSequence] = useState([]);
  const [loadingUnitLessonSequence, setLoadingUnitLessonSequence] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingGuide, setLoadingGuide] = useState(false);
  const [selectedTab, setSelectedTab] = useState("overview");
  const [strategyPickerOpen, setStrategyPickerOpen] = useState(false);
  const [strategyPickerQuery, setStrategyPickerQuery] = useState("");
  const [strategyPickerRows, setStrategyPickerRows] = useState([]);
  const [strategyPickerLoading, setStrategyPickerLoading] = useState(false);
  const [strategyPickerError, setStrategyPickerError] = useState("");
  const [addingStrategyId, setAddingStrategyId] = useState(null);
  const [strategyPickerStatus, setStrategyPickerStatus] = useState("");

  useEffect(() => {
    if (!lessonId) return;

    const loadLessonData = async () => {
      setLoading(true);
      try {
        const lessonResponse = await lessonAPI.getById(lessonId);
        setLesson(lessonResponse.data);

        // Load assets
        const assetsResponse = await assetAPI.getAll({ lessonId });
        setAssets(assetsResponse.data);
      } catch (error) {
        console.error("Error loading lesson:", error);
      } finally {
        setLoading(false);
      }
    };

    loadLessonData();
  }, [lessonId]);

  useEffect(() => {
    if (!lesson?.course_id || !lesson?.unit_number) {
      setGuide(null);
      setGuideTranscript("");
      return;
    }

    let cancelled = false;
    const loadGuideData = async () => {
      setLoadingGuide(true);
      try {
        const response = await unitGuideAPI.getByUnit(lesson.course_id, lesson.unit_number);
        const dbGuide = response.data || null;
        const fallbackGuide = getGuideByCourseAndUnit(
          lesson.course_name,
          lesson.unit_number,
        );
        const resolvedGuide = dbGuide || fallbackGuide || null;

        if (cancelled) return;
        setGuide(resolvedGuide);

        if (resolvedGuide?.transcriptPath) {
          try {
            const transcriptResponse = await fetch(toAssetUrl(resolvedGuide.transcriptPath));
            const transcriptText = transcriptResponse.ok ? await transcriptResponse.text() : "";
            if (!cancelled) {
              setGuideTranscript(transcriptText || "Transcript unavailable.");
            }
          } catch (_error) {
            if (!cancelled) setGuideTranscript("Transcript unavailable.");
          }
        } else {
          setGuideTranscript("");
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading lesson guide context:", error);
        const fallbackGuide = getGuideByCourseAndUnit(
          lesson.course_name,
          lesson.unit_number,
        );
        setGuide(fallbackGuide || null);
        setGuideTranscript("");
      } finally {
        if (!cancelled) setLoadingGuide(false);
      }
    };

    loadGuideData();
    return () => {
      cancelled = true;
    };
  }, [lesson?.course_id, lesson?.course_name, lesson?.unit_number]);

  useEffect(() => {
    if (!lesson?.unit_id) {
      setUnitLessonSequence([]);
      return;
    }

    let cancelled = false;
    const loadUnitLessonSequence = async () => {
      setLoadingUnitLessonSequence(true);
      try {
        const response = await lessonAPI.getAll({ unitId: lesson.unit_id });
        const ordered = [...(response.data || [])].sort(compareLessonsForNavigation);
        if (!cancelled) {
          setUnitLessonSequence(ordered);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error loading lesson navigation sequence:", error);
          setUnitLessonSequence([]);
        }
      } finally {
        if (!cancelled) setLoadingUnitLessonSequence(false);
      }
    };

    loadUnitLessonSequence();
    return () => {
      cancelled = true;
    };
  }, [lesson?.unit_id]);

  useEffect(() => {
    if (!lesson?.course_id) {
      setScopedFormulas([]);
      return;
    }

    let cancelled = false;

    const loadScopedFormulas = async () => {
      try {
        const scopedTeksId = lesson.primary_teks_id || lesson.teks_id || null;
        const response = await formulaAPI.getAll(lesson.course_id, scopedTeksId);
        if (!cancelled) {
          setScopedFormulas(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error loading scoped formulas:", error);
          setScopedFormulas([]);
        }
      }
    };

    loadScopedFormulas();
    return () => {
      cancelled = true;
    };
  }, [lesson?.course_id, lesson?.primary_teks_id, lesson?.teks_id]);

  const loadPersonalResources = useCallback(async () => {
    if (!lesson?.id) {
      setPersonalResources([]);
      setPersonalResourcesError("");
      return;
    }

    setLoadingPersonalResources(true);
    setPersonalResourcesError("");
    try {
      const response = await overlayAPI.getAll({ lessonId: lesson.id });
      const rows = Array.isArray(response?.data) ? response.data : [];
      setPersonalResources(rows);
    } catch (error) {
      setPersonalResources([]);
      if (error?.response?.status === 401) {
        setPersonalResourcesError("Sign in to save and view your personal resources.");
      } else {
        console.error("Error loading personal resources:", error);
        setPersonalResourcesError("Could not load personal resources right now.");
      }
    } finally {
      setLoadingPersonalResources(false);
    }
  }, [lesson?.id]);

  useEffect(() => {
    loadPersonalResources();
  }, [loadPersonalResources]);

  const deletePersonalResource = useCallback(
    async (overlayId) => {
      if (!overlayId) return;
      setDeletingResourceId(overlayId);
      setPersonalResourcesError("");
      try {
        await overlayAPI.delete(overlayId);
        setPersonalResources((prev) => prev.filter((item) => item.id !== overlayId));
      } catch (error) {
        if (error?.response?.status === 401) {
          setPersonalResourcesError("Sign in to manage personal resources.");
        } else {
          console.error("Error deleting personal resource:", error);
          setPersonalResourcesError("Could not delete this resource. Try again.");
        }
      } finally {
        setDeletingResourceId(null);
      }
    },
    [],
  );

  const openStrategyPicker = useCallback(() => {
    setStrategyPickerOpen(true);
    setStrategyPickerQuery("");
    setStrategyPickerRows([]);
    setStrategyPickerError("");
    setAddingStrategyId(null);
  }, []);

  const closeStrategyPicker = useCallback(() => {
    setStrategyPickerOpen(false);
    setStrategyPickerQuery("");
    setStrategyPickerRows([]);
    setStrategyPickerError("");
    setStrategyPickerLoading(false);
    setAddingStrategyId(null);
  }, []);

  const addStrategyToMyResources = useCallback(
    async (strategyRow) => {
      if (!lesson?.id || !strategyRow?.id) return;
      setAddingStrategyId(strategyRow.id);
      setStrategyPickerError("");
      try {
        const response = await teacherAPI.getStrategyLearnMore(strategyRow.id);
        const payload = response?.data || {};
        const strategy = payload?.strategy || {};
        const learnMore = payload?.learnMore || {};
        const notes = buildStrategyResourceNote(strategyRow, strategy, learnMore);
        const resourceUrl = String(
          learnMore?.source_url || strategy?.reference_url || strategyRow?.reference_url || "",
        ).trim();

        await overlayAPI.create({
          lesson_id: lesson.id || null,
          teks_id: lesson.primary_teks_id || lesson.teks_id || null,
          resource_url: resourceUrl,
          notes,
        });

        await loadPersonalResources();
        setStrategyPickerStatus(
          `Added "${strategy?.title || strategyRow?.title || "strategy"}" to My Resources.`,
        );
        closeStrategyPicker();
      } catch (error) {
        setStrategyPickerError(
          String(error?.response?.data?.error || "Could not add this strategy right now."),
        );
      } finally {
        setAddingStrategyId(null);
      }
    },
    [
      closeStrategyPicker,
      lesson?.id,
      lesson?.primary_teks_id,
      lesson?.teks_id,
      loadPersonalResources,
    ],
  );

  useEffect(() => {
    if (!strategyPickerOpen) return;

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setStrategyPickerLoading(true);
      setStrategyPickerError("");
      try {
        const response = await teacherAPI.listStrategies({
          q: strategyPickerQuery || undefined,
        });
        if (cancelled) return;
        setStrategyPickerRows(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        if (cancelled) return;
        setStrategyPickerRows([]);
        setStrategyPickerError(
          String(error?.response?.data?.error || "Could not load strategies right now."),
        );
      } finally {
        if (!cancelled) setStrategyPickerLoading(false);
      }
    }, 220);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [strategyPickerOpen, strategyPickerQuery]);

  useEffect(() => {
    setStrategyPickerStatus("");
  }, [lesson?.id]);

  const currentLessonIndex = useMemo(() => {
    if (!lesson?.id) return -1;
    return unitLessonSequence.findIndex((item) => item.id === lesson.id);
  }, [lesson?.id, unitLessonSequence]);

  const previousLesson = useMemo(() => {
    if (currentLessonIndex <= 0) return null;
    return unitLessonSequence[currentLessonIndex - 1] || null;
  }, [currentLessonIndex, unitLessonSequence]);

  const nextLesson = useMemo(() => {
    if (currentLessonIndex < 0) return null;
    return unitLessonSequence[currentLessonIndex + 1] || null;
  }, [currentLessonIndex, unitLessonSequence]);

  const navigateToLesson = useCallback(
    (targetLessonId) => {
      if (!targetLessonId || targetLessonId === lessonId) return;
      if (typeof onNavigateLesson === "function") {
        onNavigateLesson(targetLessonId);
        return;
      }
      window.location.hash = `/lesson/${targetLessonId}`;
    },
    [lessonId, onNavigateLesson],
  );

  const parseEssentialQuestions = (essentialQuestions) => {
    if (!essentialQuestions) return [];
    return essentialQuestions
      .split("?")
      .filter((q) => q.trim())
      .map((q) => `${q.trim()}?`);
  };

  const buildPostedObjective = (lessonData) => {
    if (!lessonData) return "";
    const teksLabel = lessonData.teks_code
      ? `TEKS ${lessonData.teks_code}`
      : "the target TEKS";
    return `Students will demonstrate understanding of ${teksLabel} through the lesson "${lessonData.lesson_title}".`;
  };

  const groupAssetsByType = (assets) => {
    return assets.reduce((acc, asset) => {
      if (!acc[asset.type]) {
        acc[asset.type] = [];
      }
      acc[asset.type].push(asset);
      return acc;
    }, {});
  };

  const getYouTubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /[?&]v=([^&#]+)/,
      /youtu\.be\/([^?&#]+)/,
      /youtube\.com\/embed\/([^?&#]+)/,
      /youtube\.com\/shorts\/([^?&#]+)/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const typeBadgeColor = (type) => {
    const colors = ["blue", "green", "amber", "purple", "rose", "teal"];
    let hash = 0;
    const str = String(type || "");
    for (let i = 0; i < str.length; i++) {
      hash = (hash * 31 + str.charCodeAt(i)) & 0xffffffff;
    }
    return colors[Math.abs(hash) % colors.length];
  };

  if (loading) {
    return (
      <div className="deep-dive-workspace">
        <div className="workspace-panel workspace-state">
          <div className="loading">Loading lesson details...</div>
        </div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="deep-dive-workspace">
        <div className="workspace-panel workspace-state">
          <div className="error">No lesson selected</div>
        </div>
      </div>
    );
  }

  const essentialQuestions = parseEssentialQuestions(lesson.essential_questions);
  const postedObjective = buildPostedObjective(lesson);
  const assetsByType = groupAssetsByType(assets);
  const formulaDomain = resolveFormulaDomain(lesson.course_name);
  const fallbackFormulas = formulaDomain ? COURSE_FORMULAS[formulaDomain] || [] : [];
  const formulas =
    scopedFormulas.length > 0
      ? scopedFormulas.map((formula) => ({
          label: formula.label,
          latex: normalizeFormulaLatex(formula.latex),
          description: formula.description || "",
        }))
      : fallbackFormulas.map((formula) => ({
          ...formula,
          latex: normalizeFormulaLatex(formula.latex),
        }));
  const showFormulaBar = formulas.length > 0;
  const showGuideTab = Boolean(guide);
  const activeTab =
    !showFormulaBar && selectedTab === "formulas"
      ? "overview"
      : !showGuideTab && selectedTab === "guide-content"
        ? "overview"
        : selectedTab;
  const relevantGuideSections = getRelevantGuideSections(guide, lesson);
  const hasPrevLesson = Boolean(previousLesson?.id);
  const hasNextLesson = Boolean(nextLesson?.id);
  const alignedStandards = Array.isArray(lesson.standards) ? lesson.standards : [];
  const instructionalSupports = Array.isArray(lesson.instructional_supports)
    ? lesson.instructional_supports
    : [];

  return (
    <div className="deep-dive-workspace">
      <div className="workspace-panel">
        <button
          type="button"
          className="lesson-nav-arrow lesson-nav-arrow-left"
          onClick={() => navigateToLesson(previousLesson?.id)}
          disabled={!hasPrevLesson || loadingUnitLessonSequence}
          aria-label={
            hasPrevLesson
              ? `Open previous lesson: ${previousLesson.lesson_title}`
              : "No previous lesson"
          }
          title={
            hasPrevLesson
              ? `Previous: ${previousLesson.lesson_title}`
              : "No previous lesson"
          }
        >
          <span aria-hidden="true">‹</span>
        </button>
        <button
          type="button"
          className="lesson-nav-arrow lesson-nav-arrow-right"
          onClick={() => navigateToLesson(nextLesson?.id)}
          disabled={!hasNextLesson || loadingUnitLessonSequence}
          aria-label={
            hasNextLesson
              ? `Open next lesson: ${nextLesson.lesson_title}`
              : "No next lesson"
          }
          title={hasNextLesson ? `Next: ${nextLesson.lesson_title}` : "No next lesson"}
        >
          <span aria-hidden="true">›</span>
        </button>

        <div className="workspace-header" data-tour="lesson-header">
          <button className="close-btn" onClick={onClose} data-tour="lesson-close" aria-label="Close lesson viewer">
            Close
          </button>
          <h1>{lesson.lesson_title}</h1>
          <p className="teks-code">
            TEKS {lesson.teks_code}: {lesson.teks_description}
          </p>
          {alignedStandards.length > 1 ? (
            <p className="chapter-ref">
              Also aligned:{" "}
              {alignedStandards
                .slice(1)
                .map((standard) => standard.teks_code)
                .filter(Boolean)
                .join(", ")}
            </p>
          ) : null}
          <p className="chapter-ref">Chapter: {lesson.chapter_ref}</p>
          {currentLessonIndex >= 0 && unitLessonSequence.length > 0 ? (
            <p className="chapter-ref">
              Lesson {currentLessonIndex + 1} of {unitLessonSequence.length} in this unit
            </p>
          ) : null}
        </div>

        <div className="tabs" data-tour="lesson-tabs">
          <button
            className={`tab-btn ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => setSelectedTab("overview")}
          >
            Overview
          </button>
          {showFormulaBar ? (
            <button
              className={`tab-btn ${activeTab === "formulas" ? "active" : ""}`}
              onClick={() => setSelectedTab("formulas")}
            >
              Formula Bar
            </button>
          ) : null}
          <button
            className={`tab-btn ${activeTab === "resources" ? "active" : ""}`}
            onClick={() => setSelectedTab("resources")}
          >
            Resources
          </button>
          <button
            className={`tab-btn ${activeTab === "my-resources" ? "active" : ""}`}
            onClick={() => setSelectedTab("my-resources")}
          >
            My Resources
          </button>
          <button
            className={`tab-btn ${activeTab === "pedagogy" ? "active" : ""}`}
            onClick={() => setSelectedTab("pedagogy")}
          >
            Guardrails
          </button>
          {showGuideTab ? (
            <button
              className={`tab-btn ${activeTab === "guide-content" ? "active" : ""}`}
              onClick={() => setSelectedTab("guide-content")}
            >
              Guide Content
            </button>
          ) : null}
        </div>

        <div className="tab-content" data-tour="lesson-tab-content">
          {activeTab === "overview" && (
            <div className="overview-section">
              <div className="i-can-statements">
                <h3>Posted Classroom Objective (Required)</h3>
                <p>
                  <strong>{lesson.teks_code ? `TEKS ${lesson.teks_code}` : "TEKS"}:</strong>{" "}
                  {lesson.teks_description || "No TEKS description available"}
                </p>
                {alignedStandards.length > 1 ? (
                  <p>
                    <strong>Full standard set:</strong>{" "}
                    {alignedStandards
                      .map((standard) => standard.teks_code)
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                ) : null}
                <p>{postedObjective}</p>
              </div>

              <div className="essential-questions">
                <h3>Success Criteria / I Can</h3>
                {essentialQuestions.length > 0 ? (
                  <ul>
                    {essentialQuestions.map((question, idx) => (
                      <li key={idx}>I can answer: {question}</li>
                    ))}
                  </ul>
                ) : (
                  <p>No essential questions defined</p>
                )}
              </div>
            </div>
          )}

          {activeTab === "formulas" && showFormulaBar && (
            <div className="formulas-section">
              <h3>Key Formulas & Equations</h3>
              <div className="formula-grid">
                {formulas.map((formula, idx) => (
                  <div key={idx} className="formula-card">
                    <div className="formula-label">{formula.label}</div>
                    <div className="formula-content">
                      <BlockMath
                        math={formula.latex}
                        renderError={() => <code>{formula.latex}</code>}
                      />
                    </div>
                    {formula.description ? (
                      <p className="formula-description">{formula.description}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "resources" && (
            <div className="resources-section">
              <h3>Instructional Suite</h3>
              {Object.keys(assetsByType).length === 0 ? (
                <p>No resources available for this lesson</p>
              ) : (
                Object.entries(assetsByType).map(([type, items]) => (
                  <div key={type} className="asset-group">
                    <div className="asset-list">
                      {items.map((asset) => {
                        const ytId = getYouTubeId(asset.url);
                        const color = typeBadgeColor(asset.type);
                        return (
                          <div key={asset.id} className="asset-card">
                            <div className="asset-card-header">
                              {asset.type && (
                                <span className={`asset-type-badge asset-type-${color}`}>
                                  {asset.type}
                                </span>
                              )}
                              <h5>{asset.title}</h5>
                            </div>
                            {ytId ? (
                              <div className="asset-video-wrap">
                                <iframe
                                  className="asset-video"
                                  src={`https://www.youtube.com/embed/${ytId}`}
                                  title={asset.title}
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                />
                              </div>
                            ) : null}
                            {asset.instructions && (
                              <p className="asset-instructions">{asset.instructions}</p>
                            )}
                            {asset.url && !ytId && (
                              <a
                                href={asset.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="asset-link"
                              >
                                Open Resource
                              </a>
                            )}
                            {asset.url && ytId && (
                              <a
                                href={asset.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="asset-link asset-link-secondary"
                              >
                                Open in YouTube
                              </a>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === "my-resources" && (
            <div className="resources-section">
              <h3>My Resources</h3>
              <div className="personal-resources-block standalone">
                <div className="personal-resources-header">
                  <h4>Teacher Links and Notes</h4>
                  <div className="personal-resources-actions">
                    <ResourceSnapper
                      lessonId={lesson.id}
                      teksId={lesson.primary_teks_id || lesson.teks_id || null}
                      onSuccess={loadPersonalResources}
                    />
                    <button
                      type="button"
                      className="nav-lite-btn"
                      onClick={openStrategyPicker}
                      disabled={!lesson?.id}
                    >
                      Add Strategy
                    </button>
                  </div>
                </div>

                {strategyPickerStatus ? (
                  <p className="resources-success">{strategyPickerStatus}</p>
                ) : null}
                {loadingPersonalResources && <p>Loading personal resources...</p>}
                {!loadingPersonalResources && personalResourcesError && (
                  <p className="resources-error">{personalResourcesError}</p>
                )}
                {!loadingPersonalResources && !personalResourcesError && personalResources.length === 0 && (
                  <p className="muted-note">No personal resources added for this lesson yet.</p>
                )}

                {!loadingPersonalResources && !personalResourcesError && personalResources.length > 0 && (
                  <div className="asset-list">
                    {personalResources.map((resource) => (
                      <article key={resource.id} className="asset-card">
                        <h5>{resource.notes ? "Teacher note" : "Saved resource"}</h5>
                        {resource.notes ? (
                          <p className="asset-instructions">{resource.notes}</p>
                        ) : (
                          <p className="asset-instructions">No notes added.</p>
                        )}
                        {resource.resource_url ? (
                          <a
                            href={resource.resource_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="asset-link"
                          >
                            Open Resource
                          </a>
                        ) : null}
                        {resource.created_at ? (
                          <p className="resource-timestamp">
                            Added {formatDateTime(resource.created_at)}
                          </p>
                        ) : null}
                        <button
                          type="button"
                          className="resource-delete-btn"
                          onClick={() => deletePersonalResource(resource.id)}
                          disabled={deletingResourceId === resource.id}
                        >
                          {deletingResourceId === resource.id ? "Removing..." : "Remove"}
                        </button>
                      </article>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "pedagogy" && (
            <div className="pedagogy-section">
              <h3>Common Misconceptions & Vocabulary</h3>
              {lesson.pedagogy ? (
                <>
                  <div className="misconceptions">
                    <h4>Common Misconceptions to Address</h4>
                    <p>{lesson.pedagogy.misconceptions}</p>
                  </div>

                  <div className="academic-vocabulary">
                    <h4>Academic Vocabulary</h4>
                    <div className="vocab-categories">
                      <div className="vocab-cat">
                        <strong>Prior Knowledge:</strong>
                        <p>{lesson.pedagogy.academic_vocabulary_prior}</p>
                      </div>
                      <div className="vocab-cat">
                        <strong>New Terms (This Lesson):</strong>
                        <p>{lesson.pedagogy.academic_vocabulary_lesson}</p>
                      </div>
                      <div className="vocab-cat">
                        <strong>Supporting Vocabulary:</strong>
                        <p>{lesson.pedagogy.academic_vocabulary_supporting}</p>
                      </div>
                    </div>
                  </div>

                  {instructionalSupports.length > 0 ? (
                    <div className="academic-vocabulary">
                      <h4>Instructional Supports</h4>
                      <div className="vocab-categories">
                        {instructionalSupports.map((support) => (
                          <div
                            className="vocab-cat"
                            key={`${support.id || support.display_order}-${support.support_type}`}
                          >
                            <strong>
                              {support.support_type} ({support.learner_group || "all"})
                            </strong>
                            <p>{support.strategy}</p>
                            {support.success_criteria ? (
                              <p>
                                <strong>Success criteria:</strong> {support.success_criteria}
                              </p>
                            ) : null}
                            {support.evidence_method ? (
                              <p>
                                <strong>Evidence:</strong> {support.evidence_method}
                              </p>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </>
              ) : (
                <p>No pedagogical data available for this lesson</p>
              )}
            </div>
          )}

          {activeTab === "guide-content" && showGuideTab && (
            <div className="guide-content-section">
              <h3>Unit Guide Context</h3>
              <div className="guide-meta-row">
                <p>
                  <strong>Guide:</strong> {guide.title || `Unit ${lesson.unit_number} Guide`}
                </p>
                {guide.sourceUrl ? (
                  <a
                    className="asset-link"
                    href={toAssetUrl(guide.sourceUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open Source PDF
                  </a>
                ) : null}
              </div>

              {loadingGuide ? <p>Loading guide context...</p> : null}

              {!loadingGuide && relevantGuideSections.length > 0 ? (
                <div className="guide-section-list">
                  {relevantGuideSections.map((section) => (
                    <div key={section.id || section.heading} className="guide-section-card">
                      <h4>{section.heading}</h4>
                      {(section.paragraphs || []).map((paragraph, idx) => (
                        <p key={`${section.id || "section"}-p-${idx}`}>{paragraph}</p>
                      ))}
                      {(section.bullets || []).length > 0 ? (
                        <ul>
                          {section.bullets.map((bullet, idx) => (
                            <li key={`${section.id || "section"}-b-${idx}`}>{bullet}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}

              {!loadingGuide && relevantGuideSections.length === 0 ? (
                <p>No structured guide sections are available for this lesson's unit.</p>
              ) : null}

              <details className="guide-transcript-details">
                <summary>Full Guide Transcript</summary>
                <pre className="guide-transcript-pre">
                  {guideTranscript || "Transcript unavailable."}
                </pre>
              </details>
            </div>
          )}
        </div>
      </div>

      {strategyPickerOpen ? (
        <div
          className="deep-strategy-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Add strategy to my resources"
          onClick={closeStrategyPicker}
        >
          <div className="deep-strategy-modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="deep-strategy-modal-header">
              <h3>Add Strategy</h3>
              <button type="button" className="nav-lite-btn" onClick={closeStrategyPicker}>
                Close
              </button>
            </div>

            <p className="muted-note">
              Pick a district or personal strategy to save into this lesson&apos;s My Resources.
            </p>
            <input
              type="search"
              value={strategyPickerQuery}
              onChange={(event) => setStrategyPickerQuery(event.target.value)}
              placeholder="Search strategies"
              aria-label="Search strategies"
            />

            {strategyPickerLoading ? <p>Loading strategies...</p> : null}
            {!strategyPickerLoading && strategyPickerError ? (
              <p className="resources-error">{strategyPickerError}</p>
            ) : null}
            {!strategyPickerLoading && !strategyPickerError && strategyPickerRows.length === 0 ? (
              <p className="muted-note">No strategies matched this search.</p>
            ) : null}
            {!strategyPickerLoading && !strategyPickerError && strategyPickerRows.length > 0 ? (
              <div className="deep-strategy-list">
                {strategyPickerRows.map((strategyRow) => (
                  <article key={strategyRow.id} className="deep-strategy-item">
                    <div>
                      <h4>{strategyRow.title}</h4>
                      {strategyRow.description ? <p>{strategyRow.description}</p> : null}
                      <div className="deep-strategy-meta">
                        <span>{String(strategyRow.category || "").replace(/_/g, " ")}</span>
                        <span>
                          {strategyRow.visibility_scope === "teacher" ? "My strategy" : "District"}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="primary-btn"
                      onClick={() => addStrategyToMyResources(strategyRow)}
                      disabled={addingStrategyId === strategyRow.id}
                    >
                      {addingStrategyId === strategyRow.id ? "Adding..." : "Add"}
                    </button>
                  </article>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DeepDiveWorkspace;
